// Общая часть браузерных Hawk-катчеров (web / admin / mobile-WebView).
//
// Три поверхности шлют события в ТРИ РАЗНЫХ проекта Hawk (свой токен на
// каждую), но правила сбора у них одни: что зачищать перед отправкой, какие
// хлебные крошки собирать, какие автодетекторы включать. Раньше эти правила
// existовали копиями в apps/web и apps/admin и разошлись (на mobile scrub
// вообще отсутствовал — PII уезжал наружу). Здесь они живут один раз.
//
// Модуль намеренно НЕ импортирует `@hawk.so/javascript`: пакета нет в
// зависимостях `@studyhub/shared`, а формы опций достаточно описать
// структурно — вызывающая сторона передаёт результат в конструктор Catcher.

import { createHawkThrottle } from "./hawk-throttle.js";

const SENSITIVE_KEY_RE =
	/^(password|token|secret|api[_-]?key|authorization|cookie|pkce[_-]?verifier|refresh[_-]?token|access[_-]?token|auth-token)$/i;
const EMAIL_RE = /\b[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}\b/g;
const PHONE_RE = /\+?\d{1,3}[\s-]?\(?\d{3,4}\)?[\s-]?\d{3,4}[\s-]?\d{2,4}/g;
const CARD_RE = /\b(?:\d[ -]*?){13,19}\b/g;

function redactStr(s: string): string {
	return s.replace(CARD_RE, "<card>").replace(EMAIL_RE, "<email>").replace(PHONE_RE, "<phone>");
}

/**
 * Глубокий PII-scrub события/контекста (ТЗ §3.7). Циклы и `undefined`
 * срезаются JSON round-trip'ом — он же приводит объект к Json-форме, которую
 * ждёт Hawk.
 */
export function scrubPII<T>(event: T): T {
	try {
		return JSON.parse(JSON.stringify(event), (key: string, value: unknown) => {
			if (typeof key === "string" && SENSITIVE_KEY_RE.test(key)) return "<redacted>";
			if (typeof value === "string") return redactStr(value);
			return value;
		}) as T;
	} catch {
		return event;
	}
}

// ── Гейт отправки: шум и шторм ────────────────────────────────────────────
//
// window.onerror ловит не только наши дефекты. Часть событий — заведомый шум,
// который браузер выбрасывает по спецификации и который ничего не значит для
// пользователя: предупреждение ResizeObserver о недоставленных уведомлениях
// (браузер сам доставит их следующим кадром), безымянный `Script error.` от
// кросс-доменного скрипта, отмена view transition новой навигацией
// (`Old view transition aborted by new view transition.` — пользователь
// кликнул второй раз, пока шла анимация первого перехода), падения внутри
// браузерных расширений. Такие
// события заводят issue, за которым нет работы, и вытесняют настоящие
// дефекты из ленты (инцидент #14: 51 повтор ResizeObserver с одной вкладки).
//
// Второй источник мусора — шторм: автоматические события SDK шли мимо
// `createHawkThrottle`, который до этого применялся только к ручным вызовам
// `captureClientError`. Одна залипшая петля в эффекте выжигала квоту проекта.

/** Минимум формы события, по которому принимается решение об отправке. */
export interface BrowserEventLike {
	title?: string;
	backtrace?: { file?: string; line?: number }[];
}

/** Схемы, из которых грузятся браузерные расширения. */
const EXTENSION_URL_RE =
	/^(?:chrome|chrome-untrusted|moz|safari-web|safari|ms-browser|opera)-extension:\/\//i;

/**
 * Сообщения, которые никогда не являются дефектом приложения. Сверяем по
 * нормализованному заголовку, потому что браузеры приписывают к нему свои
 * префиксы (`Uncaught`, `Uncaught (in promise)`, `Error:`).
 */
const NOISE_TITLE_RE =
	/^resizeobserver loop (?:limit exceeded|completed with undelivered notifications)\.?$|^script error\.?$|^old view transition aborted by new view transition\.?$/;

function normalizeTitle(title: string): string {
	return title
		.replace(/^uncaught(?: \(in promise\))?:?\s*/i, "")
		.replace(/^(?:[a-z]*error):\s*/i, "")
		.trim()
		.toLowerCase();
}

/**
 * Событие — заведомый шум? Расширение опознаём по стеку целиком: если хотя бы
 * один кадр наш, ошибка могла прийти из нашего кода, и глушить её нельзя.
 */
export function isBrowserNoiseEvent(event: BrowserEventLike): boolean {
	if (event.title && NOISE_TITLE_RE.test(normalizeTitle(event.title))) return true;
	const frames = event.backtrace ?? [];
	return frames.length > 0 && frames.every((frame) => EXTENSION_URL_RE.test(frame.file ?? ""));
}

/**
 * Отпечаток для дедупа: заголовок + первый кадр. Тот же принцип, что у
 * `hawkFingerprint`, но считается по уже собранному событию SDK, у которого
 * стека-строки нет.
 */
export function browserEventFingerprint(event: BrowserEventLike): string {
	const frame = event.backtrace?.[0];
	return `${event.title ?? ""}|${frame?.file ?? ""}:${frame?.line ?? ""}`;
}

/**
 * `beforeSend` для браузерного катчера: сначала гейт (шум → шторм), потом
 * PII-scrub. Возврат `false` отменяет отправку — так это понимает SDK.
 * Состояние троттла живёт в замыкании, поэтому вызывать на инициализации
 * катчера один раз.
 */
export function createBrowserBeforeSend(): <T extends BrowserEventLike>(event: T) => T | false {
	const allowSend = createHawkThrottle();
	return <T extends BrowserEventLike>(event: T): T | false => {
		if (isBrowserNoiseEvent(event)) return false;
		if (!allowSend(browserEventFingerprint(event))) return false;
		return scrubPII(event);
	};
}

/**
 * Порог Long Task. Стандартные 100 мс на нашем UI дают шум (первый рендер
 * воркспейса, парсинг большого конспекта), поэтому репортим только настоящие
 * фризы — те, что человек уже воспринимает как «подвисло».
 */
export const LONG_TASK_THRESHOLD_MS = 500;

/** Сколько крошек держим. Дефолт SDK — 15, нам нужен более длинный след. */
export const MAX_BREADCRUMBS = 30;

/**
 * Общие опции сбора для браузерного катчера. Токен, release и context —
 * на стороне поверхности, здесь только правила.
 *
 * Web Vitals НЕ включаем сознательно: Hawk — трекер ошибок, метрики нам
 * сейчас некуда сводить (PostHog в проде не подключён). Long Tasks оставлены —
 * это фризы главного потока, то есть дефект, а не метрика.
 */
export function browserCatcherCollectionOptions(): {
	consoleTracking: boolean;
	breadcrumbs: {
		maxBreadcrumbs: number;
		trackFetch: boolean;
		trackNavigation: boolean;
		trackClicks: boolean;
		beforeBreadcrumb: <T>(breadcrumb: T) => T;
	};
	issues: {
		errors: boolean;
		longTasks: { thresholdMs: number };
		webVitals: boolean;
		longAnimationFrames: boolean;
	};
} {
	return {
		// console.error/warn становятся крошками — видно, что писал код перед падением.
		consoleTracking: true,
		breadcrumbs: {
			maxBreadcrumbs: MAX_BREADCRUMBS,
			trackFetch: true,
			trackNavigation: true,
			trackClicks: true,
			// Крошки собираются ДО beforeSend и содержат URL запросов и тексты
			// кликнутых элементов — то есть тот же PII. Чистим здесь же.
			beforeBreadcrumb: (breadcrumb) => scrubPII(breadcrumb),
		},
		issues: {
			errors: true,
			longTasks: { thresholdMs: LONG_TASK_THRESHOLD_MS },
			webVitals: false,
			longAnimationFrames: false,
		},
	};
}

// ── Реестр активного катчера ──────────────────────────────────────────────
//
// В Capacitor-сборке WebView грузит бандл apps/web, но катчером владеет
// mobile-shell (свой проект Hawk). При этом код, который сообщает об ошибках,
// живёт в apps/web (ErrorBoundary, PowerSync, офлайн-очередь) и знает только
// про web-клиент. Без общей точки его вызовы в APK уходили бы в никуда —
// вместе с привязкой affected-user.
//
// Этот модуль в бандле один, поэтому синглтон здесь общий для обеих сборок:
// поверхность регистрирует свой инстанс, а вызывающий код всегда попадает в
// тот катчер, который в этой сборке действительно инициализирован.

/** Минимум, который нужен вызывающему коду. Структурный аналог Catcher. */
export interface BrowserCatcherLike {
	send(message: Error | string, context?: unknown): void;
	setUser(user: { id: string }): void;
	clearUser(): void;
	breadcrumbs: { add(breadcrumb: unknown): void };
}

let activeCatcher: BrowserCatcherLike | null = null;

/** Объявить катчер этой сборки активным (вызывает поверхность при init). */
export function registerBrowserCatcher(catcher: BrowserCatcherLike | null): void {
	activeCatcher = catcher;
}

/** Активный катчер сборки — или null, если Hawk не сконфигурирован. */
export function activeBrowserCatcher(): BrowserCatcherLike | null {
	return activeCatcher;
}
